import React, { useEffect, useState } from 'react';
import {
    Cell, Pie, PieChart, ResponsiveContainer, Tooltip,
} from 'recharts';
import { connect } from 'react-redux';
import { buildReportsSelector, buildMetaReportsSelector } from 'store/selectors/memberReports.selector';
import { loadTopHotels } from 'store/dispatchers/memberReports.dispatchers';
import PropTypes from 'prop-types';
import Row from 'components/UI/Layout/Row';
import Col from 'components/UI/Layout/Col';
import _, {first, get, values} from 'lodash';
import Container from 'components/UI/Layout/Container';
import Button from 'components/UI/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import Skeleton from 'components/UI/Skeleton';
import { formatAmount } from 'utils/formatters';

function CustomTooltip({ active, payload })
{
    if (active && payload && payload.length)
    {
        return (
            <div className="tw-bg-black tw-opacity-80 tw-text-white tw-px-4 tw-py-6 tw-rounded">
                <p className="label">{`${payload[0].payload.record.hotel} : ${formatAmount(payload[0].value, 'USD')}`}</p>
            </div>
        );
    }

    return null;
}
CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.any,
};

function TopHotels({
    defaultPeriod, getTopHotels, getStoredTopHotelsReport, getMetaStoredTopHotelsReport,
})
{
    const [period, setPeriod] = useState(defaultPeriod);
    const [topHotels, setTopHotels] = useState([]);

    const COLORS = [
        '#ff6384',
        '#4bc0c0',
        '#ffce56',
        '#36a2eb',
        '#c14b7a',
        '#53b59f',
        '#c7d68f',
        '#662d91',
        '#8094c4',
        '#e7e9ed',
    ];

    const storedTopHotelsReport = getStoredTopHotelsReport(period);
    const metaStoredTopHotelsReport = getMetaStoredTopHotelsReport(period);
    const isLoading = get(metaStoredTopHotelsReport, 'loading', false);

    useEffect(() =>
    {
        if (period !== defaultPeriod)
        {
            setPeriod(defaultPeriod);
        }
    }, [defaultPeriod]);

    useEffect(() =>
    {
        loadDataIfNeeded();
    }, [period]);

    useEffect(() =>
    {
        if (!_.isEmpty(storedTopHotelsReport) || metaStoredTopHotelsReport?.filters?.loaded === true)
        {
            fillTopHotelsReport(storedTopHotelsReport.data);
        }
    }, [storedTopHotelsReport]);

    const fillTopHotelsReport = (topHotelsReport) =>
    {
        setTopHotels(first(values(topHotelsReport)) ?? []);
    };

    const loadDataIfNeeded = () =>
    {
        getTopHotels(period);
    };

    return (
        <Container className="tw-w-full">
            <Row className="tw-w-full tw-max-w-[630px] tw-mx-auto tw-align-middle tw-justify-center tw-mb-3">
                <Button
                    onClick={() =>
                    {
                        setPeriod('monthly');
                    }}
                    type="custom"
                    customBg={period === 'monthly' ? 'tw-bg-primary-default hover:tw-bg-primary-hover tw-text-white' : 'tw-bg-neutral-700 tw-text-white'}
                    margin="tw-mt-0"
                    rounded="tw-rounded-l"
                >Last 90 days</Button>
                <Button
                    onClick={() =>
                    {
                        setPeriod('yearly');
                    }}
                    type="custom"
                    customBg={period === 'yearly' ? 'tw-bg-primary-default hover:tw-bg-primary-hover tw-text-white' : 'tw-bg-neutral-700 tw-text-white'}
                    margin="tw-mt-0"
                    rounded="tw-rounded-r"
                >Last Year</Button>
            </Row>
            {!isLoading && topHotels.length === 0 ? (
                <Row className="tw-text-center tw-align-middle tw-justify-center">
                    <p>No <strong className="tw-capitalize">{period === 'yearly' ? 'Last year' : 'Last 90 days'}</strong> statistics available yet for</p>
                </Row>
            ) : (
                <Row className="tw-w-full tw-max-w-[630px] tw-mx-auto">
                    <Col className="tw-w-1/2 tw-py-5 tw-flex tw-justify-center tw-self-start tw-text-sm">
                        {isLoading && <Skeleton type="list" height="tw-h-[250px]" />}
                        {!isLoading && topHotels.length > 0 && (
                            <>
                                <h3 className="tw-font-semibold tw-text-base tw-text-black dark:tw-text-white">
                                    Hotels
                                </h3>
                                {topHotels.map((record, index) => (
                                    <div key={record.record.hotel} className="tw-mb-1 tw-mt-4">
                                        <FontAwesomeIcon
                                            className="tw-mr-3"
                                            color={COLORS[index % COLORS.length]}
                                            icon={faCircle}
                                        />
                                        {record.record.hotel}
                                    </div>
                                ))}
                            </>
                        )}
                    </Col>
                    <Col className="tw-max-w-[315px] tw-max-h-[315px] tw-w-1/2">
                        {isLoading ? <Skeleton type="box" height="tw-h-[250px]" />
                            : (
                                <ResponsiveContainer width="100%" height="100%" minHeight={250}>
                                    <PieChart
                                        width={330}
                                        height={330}
                                        margin={{
                                            top: 0, right: 0, bottom: 0, left: 0,
                                        }}
                                    >
                                        <Tooltip content={<CustomTooltip />} />
                                        <Pie
                                            data={topHotels}
                                            labelLine={false}
                                            innerRadius={60}
                                            dataKey="record.sales"
                                            isAnimationActive={false}
                                        >
                                            {topHotels.map((record, index) => (
                                                <Cell key={`cell-${record.record.hotel}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                    </Col>
                </Row>
            )}
        </Container>
    );
}

TopHotels.defaultProps = {
    defaultPeriod: 'monthly',
};

TopHotels.propTypes = {
    getTopHotels: PropTypes.func,
    getStoredTopHotelsReport: PropTypes.func,
    getMetaStoredTopHotelsReport: PropTypes.func,
    defaultPeriod: PropTypes.string,
};

const mapStateToProps = () =>
{
    const reportSelector = buildReportsSelector();
    const metaReportSelector = buildMetaReportsSelector();

    return (state) => ({
        getStoredTopHotelsReport: (period) => reportSelector(state, `top_hotels_report_${period}`),
        getMetaStoredTopHotelsReport: (period) => metaReportSelector(state, `top_hotels_report_${period}`),
    });
};

const mapDispatchToProps = (dispatch) => ({
    getTopHotels(period)
    {
        return dispatch(loadTopHotels(period));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(TopHotels);
